import { showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade'

/**
 * Options for opening the email composer.
 * See oap-web: src/hostEmailComposer.ts — OpenEmailComposerFromHostOptions
 *
 * @typedef {Object} OpenEmailComposerOptions
 * @property {string[]}  recipientIds           - Contact UUIDs or plain email addresses to populate in the To field.
 * @property {'standard'|'bulk'} [variant]      - Composer variant; defaults to 'standard'.
 * @property {string}   [subject]               - Pre-filled subject line.
 * @property {string}   [body]                  - Pre-filled email body.
 * @property {string[]} [ccRecipientIds]        - Contact UUIDs or plain email addresses to pre-fill in CC.
 * @property {string[]} [bccRecipientIds]       - Contact UUIDs or plain email addresses to pre-fill in BCC.
 * @property {boolean}  [isCcBccVisible]        - Whether the CC/BCC row is expanded on open.
 * @property {string}   [sendViaCalendlySource] - EnumContactInteractionLocation value used
 *                                               to tag the interaction when sending via Calendly.
 * @property {'send_bulk_email'|'new_email_hover'|'new_email_contact_details'|'new_email_interaction_feed'|null} [source]
 *   - Analytics source for the open event.
 * @property {() => void} [onSendSuccess]       - Callback fired after a successful send.
 */

/** Dispatches into oap-web's CRM store via module federation (only runs on user action).
 * @returns {(options: OpenEmailComposerOptions) => void}
 */
export const useOpenEmailComposerFromHost = () => {
  const dispatch = useDispatch()
  const enforceSmartComposer = useEnforceUpgrade('crm_smart_compose', {
    skipPreface: true,
  })

  return useCallback(
    (options) => {
      enforceSmartComposer(async () => {
        let mod
        try {
          mod = await import(
            /* webpackChunkName: "oap_web_host_email_composer" */
            'oap_web/HostStandardEmailComposer'
          )
        } catch {
          // Remote missing/outdated — intentionally no-op.
          return
        }
        try {
          await mod.openEmailComposerWithChecks?.(options)
        } catch {
          dispatch(showError(I18n.t('errors.default_no_action')))
        }
      })
    },
    [enforceSmartComposer, dispatch],
  )
}
